import EyeButton from '@/components/EyeButton.vue'
import type { SwapAlertResponse } from '@/services/alerts-v2-service/types'
import { markRaw } from 'vue'
import type { TableColumn } from '@/components/alerts/types'
import { getTradeUrl } from '@/utils'
import { useRouter } from 'vue-router'
import { getSeverityByVol24 } from './swapColumns'

export const getSwapSpotTableColumns = (): TableColumn[] => {
  const router = useRouter()
  return [
    {
      field: 'symbol.name',
      skeletonWidth: '70px',
      width: '120px',
      header: 'table.asset',
      class: 'text-left',
      dynamic: true,
      inner: 'a',
      innerPropsGenerator: (v: object) => {
        const data = v as SwapAlertResponse
        const asset = data.symbol.name.split('/')[0] as string
        const href = router.resolve({
          name: 'eye-all-asset',
          params: {
            asset: asset,
          },
        }).fullPath
        return {
          target: '_blank',
          href: href,
          class: 'pit hover',
        }
      },
    },

    {
      field: 'spread',
      width: '85px',
      header: 'table.spread',
      class: 'text-center',
      sortable: true,
      dynamic: true,
      inner: 'span',
      innerPropsGenerator: (v: object) => {
        const data = v as SwapAlertResponse
        const myClass = data.spread && data.spread > 0 ? 'success' : 'error'
        return {
          class: `pit digit ${myClass}`,
        }
      },
    },
    {
      field: 'ex_buy.name',
      width: '110px',
      header: 'table.exLong',
      class: 'text-center xl:text-right font-bold',
      dynamic: true,
      inner: 'a',
      innerPropsGenerator: (v: object) => {
        const data = v as SwapAlertResponse
        const asset = data.symbol.name.split('/')[0] as string
        const href = getTradeUrl(data.ex_buy.name, asset, true)
        return {
          target: '_blank',
          href: href,
          class: 'pit success hover',
        }
      },
    },
    {
      field: 'vol24h_buy',
      width: '105px',
      header: 'table.vol24h',
      class: 'hidden xl:table-cell text-left',
      dynamic: true,
      inner: 'span',
      innerPropsGenerator: (v: object) => {
        const data = v as SwapAlertResponse
        const myClass = getSeverityByVol24(data.vol24h_buy)
        return {
          class: `pit digit ${myClass}`,
        }
      },
    },
    {
      field: 'price_buy',
      width: '115px',
      header: 'table.price',
      class: 'hidden xl:table-cell text-center',
      inner: 'span',
      innerProps: {
        class: 'pit digit',
      },
    },
    {
      field: 'rate_long',
      width: '105px',
      header: 'table.fundingRate',
      class: 'text-center hidden xl:table-cell',
      inner: 'span',
      innerProps: {
        class: 'pit digit',
      },
    },
    {
      field: 'period_long',
      width: '115px',
      header: 'table.fundingPeriod',
      class: 'hidden xl:table-cell text-left',
      inner: 'span',
      innerProps: {
        class: 'pit digit',
      },
    },
    {
      field: 'ex_sell.name',
      width: '110px',
      header: 'table.exSell',
      class: 'text-center xl:text-right font-bold',
      dynamic: true,
      inner: 'a',
      innerPropsGenerator: (v: object) => {
        const data = v as SwapAlertResponse
        const asset = data.symbol.name.split('/')[0] as string
        const href = getTradeUrl(data.ex_sell.name, asset, false)
        return {
          href: href,
          target: '_blank',
          class: 'pit error hover',
        }
      },
    },
    {
      dynamic: true,
      field: 'vol24h_sell',
      width: '105px',
      header: 'table.vol24h',
      class: 'hidden xl:table-cell text-left',
      inner: 'span',
      innerPropsGenerator: (v: object) => {
        const data = v as SwapAlertResponse
        const myClass = getSeverityByVol24(data.vol24h_sell)
        return {
          class: `pit digit ${myClass}`,
        }
      },
    },
    {
      field: 'price_sell',
      width: '115px',
      header: 'table.price',
      class: 'hidden xl:table-cell text-left',
      inner: 'span',
      innerProps: {
        class: 'pit digit',
      },
    },
    {
      field: 'updated_at',
      width: '150px',
      header: 'table.timeAgo',
      class: 'text-center',
      skeletonWidth: '70px',
      sortable: true,
      dynamic: true,
      inner: 'span',
      innerProps: {
        class: 'pit digit',
      },
    },
    {
      dynamic: true,
      field: '',
      header: '',
      width: '50px',
      class: 'text-center collapser-class',
      noPadding: true,
      inner: markRaw(EyeButton),
      innerPropsGenerator: (data: object) => {
        const swapData = data as SwapAlertResponse
        return {
          name: 'eye',
          params: {
            asset: swapData.symbol?.name?.split('/')?.[0]?.toLowerCase() ?? '',
            exBuy: swapData['ex_buy']['name'],
            exSell: swapData['ex_sell']['name'],
            marketType: 'swap-spot',
          },
        }
      },
    },
  ]
}
